import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Linkedin, Twitter, Facebook, Youtube } from 'lucide-react';
import { companyInfo, services, industries } from '../../mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: services.slice(0, 7),
    industries: industries.slice(0, 5),
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Leadership', href: '/about/leadership' },
      { name: 'Careers', href: '/careers' },
      { name: 'Locations', href: '/about/locations' },
      { name: 'Innovation Lab', href: '/about/innovation-lab' }
    ],
    resources: [
      { name: 'Client Success', href: '/client-success' },
      { name: 'Insights', href: '/insights' },
      { name: 'Partner Ecosystem', href: '/partner-ecosystem' },
      { name: 'Contact Us', href: '/contact' }
    ]
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div>
              <img
                src="/images/logo_white.png"
                alt="Techspr Logo"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-gray-400 mb-6">{companyInfo.description}</p>
            <div className="space-y-3">
              <a
                href={`mailto:${companyInfo.contact.email}`}
                className="flex items-center space-x-2 text-sm hover:text-indigo-400 transition-colors duration-200"
              >
                <Mail className="w-4 h-4" />
                <span>{companyInfo.contact.email}</span>
              </a>
              <a
                href={`tel:${companyInfo.contact.phone}`}
                className="flex items-center space-x-2 text-sm hover:text-indigo-400 transition-colors duration-200"
              >
                <Phone className="w-4 h-4" />
                <span>{companyInfo.contact.phone}</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Services
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-sm hover:text-indigo-400 transition-colors duration-200"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services"
                  className="text-sm text-indigo-400 hover:text-indigo-300 font-medium transition-colors duration-200"
                >
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Industries
            </h3>
            <ul className="space-y-3">
              {footerLinks.industries.map((industry) => (
                <li key={industry.id}>
                  <Link
                    to={`/industries/${industry.slug}`}
                    className="text-sm hover:text-indigo-400 transition-colors duration-200"
                  >
                    {industry.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/industries"
                  className="text-sm text-indigo-400 hover:text-indigo-300 font-medium transition-colors duration-200"
                >
                  View All Industries →
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-indigo-400 transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Resources
            </h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-indigo-400 transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Links & Newsletter */}
        <div className="border-t border-gray-800 pt-8 pb-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <a
                href="https://in.linkedin.com/company/officialtechspr"
                className="text-gray-400 hover:text-indigo-400 transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/techspr/"
                className="text-gray-400 hover:text-indigo-400 transition-colors duration-200"
                aria-label="Instagram"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/techspr17"
                className="text-gray-400 hover:text-indigo-400 transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@officialtechspr"
                className="text-gray-400 hover:text-indigo-400 transition-colors duration-200"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link
                to="/privacy"
                className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200"
              >
                Terms of Use
              </Link>
              {/* <Link
                to="/cookies"
                className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200"
              >
                Cookie Policy
              </Link>
              <Link
                to="/sitemap"
                className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200"
              >
                Sitemap
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;